import { useState, useCallback, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

import { Icon } from '@components/Icon';
import { buildClassName, formatPhoneNumber, getPhoneDigits } from '@utils';

import './TextField.css';

export interface TextFieldProps {
  /**
   * Visible field label. Omit only for compact inline fields (filter bars, confirm phrases)
   * that pass `aria-label` instead.
   */
  label?: string;
  /** Whether the field is required (shows inline asterisk) */
  required?: boolean;
  /** Error message to display below the field */
  error?: string;
  /** Helper text below the field */
  helperText?: string;
  /** Success message below the field (e.g. "Passwords match"); shown when no error */
  success?: string;
  /** Input type when multiline is false */
  type?: 'text' | 'email' | 'tel' | 'number' | 'date' | 'time' | 'password';
  /** Controlled show/hide for password (sync multiple password fields: use same state for both) */
  passwordVisible?: boolean;
  /** Called when user toggles show/hide; use with passwordVisible to sync across fields */
  onPasswordVisibleChange?: (visible: boolean) => void;
  /** Use a textarea instead of input */
  multiline?: boolean;
  /** Number of rows when multiline */
  rows?: number;
  /** Full width of container */
  fullWidth?: boolean;
  /** Additional class for the root element */
  className?: string;
  /** Input/textarea id for label association */
  id?: string;
}

/** Single-line input props. `multiline` omitted or false. */
export type TextFieldInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof TextFieldProps | 'size'
> &
  TextFieldProps & { multiline?: false };

/** Multi-line textarea props. `multiline` must be true. */
export type TextFieldTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  keyof TextFieldProps
> &
  TextFieldProps & { multiline: true };

export function TextField(props: TextFieldInputProps | TextFieldTextareaProps) {
  const {
    label,
    required = false,
    error,
    helperText,
    success,
    type = 'text',
    passwordVisible: passwordVisibleProp,
    onPasswordVisibleChange,
    multiline = false,
    rows = 4,
    fullWidth = false,
    className = '',
    id: idProp,
    value: valueProp,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    disabled,
    readOnly,
    ...rest
  } = props;

  const [passwordVisibleInternal, setPasswordVisibleInternal] = useState(false);
  const [telFocused, setTelFocused] = useState(false);
  const isControlledPasswordVisible = passwordVisibleProp !== undefined;
  const passwordVisible = isControlledPasswordVisible ? passwordVisibleProp : passwordVisibleInternal;
  const generatedId = useId();
  const id = idProp ?? `bhg-text-field-${generatedId}`;
  const isPassword = type === 'password';
  const isTel = type === 'tel';
  const isControlledTel = isTel && valueProp != null;
  const effectiveType = isPassword && passwordVisible ? 'text' : type;
  const togglePasswordLabel = passwordVisible ? 'Hide password' : 'Show password';
  const togglePassword = useCallback(() => {
    const next = !passwordVisible;
    if (onPasswordVisibleChange) onPasswordVisibleChange(next);
    if (!isControlledPasswordVisible) setPasswordVisibleInternal(next);
  }, [passwordVisible, onPasswordVisibleChange, isControlledPasswordVisible]);

  const displayValue = isControlledTel
    ? formatPhoneNumber(getPhoneDigits(String(valueProp)))
    : valueProp;

  const telDigits = isTel ? getPhoneDigits(String(valueProp ?? '')) : '';
  const showTelClear =
    isTel && telFocused && telDigits.length > 0 && !disabled && !readOnly && Boolean(onChangeProp);

  const handleClearTel = useCallback(() => {
    if (!onChangeProp) return;
    const synthetic = {
      target: { value: '' },
      currentTarget: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    (onChangeProp as React.ChangeEventHandler<HTMLInputElement>)(synthetic);
  }, [onChangeProp]);

  const handleTelFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTelFocused(true);
    (onFocusProp as React.FocusEventHandler<HTMLInputElement> | undefined)?.(e);
  };

  const handleTelBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTelFocused(false);
    (onBlurProp as React.FocusEventHandler<HTMLInputElement> | undefined)?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTel && onChangeProp) {
      const digits = getPhoneDigits(e.target.value);
      const formatted = formatPhoneNumber(digits);
      const synthetic = {
        ...e,
        target: { ...e.target, value: formatted },
      } as React.ChangeEvent<HTMLInputElement>;
      (onChangeProp as React.ChangeEventHandler<HTMLInputElement>)(synthetic);
    } else if (onChangeProp) {
      (onChangeProp as React.ChangeEventHandler<HTMLInputElement>)(e);
    }
  };

  const rootClasses = buildClassName(
    'bhg-text-field',
    fullWidth && 'bhg-text-field--full-width',
    error && 'bhg-text-field--error',
    type === 'date' && 'bhg-text-field--type-date',
    isPassword && 'bhg-text-field--password',
    isTel && 'bhg-text-field--tel',
    className
  );

  const labelContent = (
    <span className="bhg-text-field__label-text">
      {label}
      {required && (
        <span className="bhg-text-field__required" aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </span>
  );

  let inputDescribedBy: string | undefined;
  if (error) inputDescribedBy = `${id}-error`;
  else if (success) inputDescribedBy = `${id}-success`;
  else if (helperText) inputDescribedBy = `${id}-helper`;

  // The textarea never points at the success message.
  let textareaDescribedBy: string | undefined;
  if (error) textareaDescribedBy = `${id}-error`;
  else if (helperText) textareaDescribedBy = `${id}-helper`;

  const inputPassthrough = {
    ...rest,
    id,
    type: effectiveType,
    className: buildClassName(
      'bhg-text-field__input',
      isPassword && 'bhg-text-field__input--with-toggle',
      showTelClear && 'bhg-text-field__input--with-toggle'
    ),
    required,
    'aria-invalid': !!error,
    'aria-describedby': inputDescribedBy,
    ...(valueProp != null
      ? { value: isControlledTel ? (displayValue ?? '') : valueProp }
      : {}),
    ...(onChangeProp
      ? { onChange: isTel ? handleChange : (onChangeProp as React.ChangeEventHandler<HTMLInputElement>) }
      : {}),
    ...(isTel
      ? {
          onFocus: handleTelFocus,
          onBlur: handleTelBlur,
        }
      : {}),
    disabled,
    readOnly,
  } as InputHTMLAttributes<HTMLInputElement>;

  let control: ReactNode;
  if (multiline) {
    control = (
      <textarea
        id={id}
        className="bhg-text-field__input bhg-text-field__textarea"
        rows={rows}
        required={required}
        aria-invalid={!!error}
        aria-describedby={textareaDescribedBy}
        {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        {...(valueProp != null ? { value: valueProp } : {})}
        {...(onChangeProp ? { onChange: onChangeProp as React.ChangeEventHandler<HTMLTextAreaElement> } : {})}
      />
    );
  } else if (isPassword) {
    control = (
      <div className="bhg-text-field__password-wrap">
        <input {...inputPassthrough} />
        <button
          type="button"
          className="bhg-text-field__password-toggle"
          onClick={togglePassword}
          aria-label={togglePasswordLabel}
          title={togglePasswordLabel}
        >
          <Icon
            type={passwordVisible ? 'eye-off' : 'eye'}
            colorVariant="gray"
            size="md"
            backgroundShape="rounded-square"
            aria-hidden
          />
        </button>
      </div>
    );
  } else if (isTel) {
    control = (
      <div className="bhg-text-field__tel-wrap">
        <input {...inputPassthrough} />
        {showTelClear ? (
          <button
            type="button"
            className="bhg-text-field__tel-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClearTel}
            aria-label="Clear phone number"
            title="Clear phone number"
          >
            <Icon type="circle-x" style="outlined" colorVariant="gray" size="md" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  } else {
    control = <input {...inputPassthrough} />;
  }

  return (
    <div className={rootClasses}>
      <label htmlFor={id} className="bhg-text-field__label">
        {labelContent}
      </label>
      {control}
      {error && (
        <p id={`${id}-error`} className="bhg-text-field__error" role="alert">
          {error}
        </p>
      )}
      {success && !error && (
        <p id={`${id}-success`} className="bhg-text-field__success" role="status">
          <Icon type="checkmark" colorVariant="green" size="sm" aria-hidden />
          <span>{success}</span>
        </p>
      )}
      {helperText && !error && !success && (
        <p id={`${id}-helper`} className="bhg-text-field__helper">
          {helperText}
        </p>
      )}
    </div>
  );
}
